

export interface MouthCue {
    start: number;
    end: number;
    value: string;
}

export interface LipsyncOutput {
    metadata: {
        soundFile: string;
        duration: number;
    };
    mouthCues: MouthCue[];
}

// Hàm tạo lipsync cơ bản cho bất kỳ ngôn ngữ nào
export function generateTextToLipsync(text: string, durationInSeconds: number): LipsyncOutput {
    const time = new Date().getTime();
    console.log(`Starting universal lipsync generation`);

    // Chia text thành các từ
    const words = text.trim().split(/\s+/);

    // Ước tính thời gian cho mỗi từ dựa trên độ dài
    const totalCharCount = words.reduce((sum, word) => sum + word.length, 0);
    const timePerChar = durationInSeconds / (totalCharCount + words.length * 0.5);

    // Mapping visemes to standard RPM/wawa-lipsync morph targets
    // viseme_sil, viseme_PP, viseme_FF, viseme_TH, viseme_DD, viseme_kk, viseme_CH, viseme_SS, viseme_nn, viseme_RR, viseme_aa, viseme_E, viseme_I, viseme_O, viseme_U
    const universalVisemeMap: Record<string, string> = {
        // P, B, M -> PP
        'p': 'viseme_PP', 'b': 'viseme_PP', 'm': 'viseme_PP',

        // F, V -> FF
        'f': 'viseme_FF', 'v': 'viseme_FF',

        // T, D, TH -> TH / DD
        't': 'viseme_TH', 'd': 'viseme_DD', 'th': 'viseme_TH',

        // K, G, H, NG -> kk
        'k': 'viseme_kk', 'g': 'viseme_kk', 'h': 'viseme_kk',

        // CH, J, SH -> CH
        'ch': 'viseme_CH', 'j': 'viseme_CH', 'sh': 'viseme_CH',

        // S, Z -> SS
        's': 'viseme_SS', 'z': 'viseme_SS',

        // N, L -> nn
        'n': 'viseme_nn', 'l': 'viseme_nn',

        // R -> RR
        'r': 'viseme_RR',

        // A -> aa
        'a': 'viseme_aa', 'á': 'viseme_aa', 'à': 'viseme_aa',

        // E -> E
        'e': 'viseme_E', 'é': 'viseme_E', 'è': 'viseme_E',

        // I -> I
        'i': 'viseme_I', 'y': 'viseme_I',

        // O -> O
        'o': 'viseme_O', 'ó': 'viseme_O', 'ò': 'viseme_O',

        // U, W -> U
        'u': 'viseme_U', 'w': 'viseme_U', 'ú': 'viseme_U', 'ù': 'viseme_U'
    };

    // Các nguyên âm cơ bản
    const vowels = ['a', 'e', 'i', 'o', 'u', 'y'];

    // Tạo các mouthCues
    const mouthCues: MouthCue[] = [];
    let currentTime = 0;

    // Xử lý từng từ
    words.forEach(word => {
        const cleanWord = word.toLowerCase().trim().replace(/[.,!?;:]/g, '');
        if (!cleanWord) return;

        const wordDuration = timePerChar * (cleanWord.length + 0.5);

        // Phân tích từ thành phần chính
        let mainViseme = 'viseme_aa'; // Mặc định mở miệng (aa) nếu không tìm thấy, tốt hơn là đóng (sil)

        // Tìm các viseme đặc biệt trước
        for (const char of cleanWord) {
            if (universalVisemeMap[char]) {
                mainViseme = universalVisemeMap[char];
                // Ưu tiên nguyên âm nếu có
                if (vowels.includes(char)) {
                    break;
                }
            }
        }

        // Có 3 giai đoạn chính cho mỗi từ:
        // 1. Mở miệng để bắt đầu nói (attack)
        mouthCues.push({
            start: currentTime,
            end: currentTime + wordDuration * 0.2,
            value: mainViseme
        });

        // 2. Duy trì vị trí miệng chính (sustain)
        mouthCues.push({
            start: currentTime + wordDuration * 0.2,
            end: currentTime + wordDuration * 0.8,
            value: mainViseme
        });

        // 3. Đóng miệng để kết thúc từ (release) - chuyển về sil hoặc giảm cường độ
        mouthCues.push({
            start: currentTime + wordDuration * 0.8,
            end: currentTime + wordDuration,
            value: 'viseme_sil'
        });

        currentTime += wordDuration;
    });

    // Hợp nhất các viseme liên tiếp giống nhau để tránh giật
    const mergedMouthCues: MouthCue[] = [];
    for (let i = 0; i < mouthCues.length; i++) {
        if (i > 0 && mouthCues[i].value === mouthCues[i - 1].value) {
            mergedMouthCues[mergedMouthCues.length - 1].end = mouthCues[i].end;
        } else {
            mergedMouthCues.push(mouthCues[i]);
        }
    }

    // Đảm bảo kết thúc đúng thời lượng
    if (mergedMouthCues.length > 0) {
        mergedMouthCues[mergedMouthCues.length - 1].end = durationInSeconds;
    }

    console.log(`Universal lipsync generated in ${new Date().getTime() - time}ms`);

    return {
        metadata: {
            soundFile: "",
            duration: durationInSeconds
        },
        mouthCues: mergedMouthCues
    };
}

export function estimateDuration(text: string): number {
    // Rough estimation: 15 characters per second (average speaking speed)
    // Minimum 1 second
    return Math.max(1, text.length / 15);
}